import type { RumBeforeSend } from '@datadog/browser-rum';

/**
 * Datadog RUM beforeSend filter
 *
 * Drops noisy, non-actionable events before they leave the browser.
 * Every rule targets a well-known category of junk that clogs RUM error
 * streams. If you add a rule, leave a short comment explaining *why*.
 *
 * Return `false` to discard the event (errors, resources, actions, long_tasks)
 * Return `true` to keep the event
 *
 * @see https://docs.datadoghq.com/real_user_monitoring/guide/enrich-and-control-rum-data/
 */
export const beforeSend: RumBeforeSend = (event) => {
  if (!isAllowedRumHost()) {
    return false;
  }

  if (event.type === 'error') {
    const message = event.error?.message || '';
    const stack = event.error?.stack || '';

    // Drop noisy errors by message
    if (NOISY_ERROR_RE.test(message)) {
      return false;
    }

    if (FETCH_ERROR_RE.test(message) && GTM_STACK_RE.test(stack)) {
      return false;
    }
  }

  if (event.type === 'resource') {
    const url = event.resource?.url || '';

    // Drop resource events for third-party domains (pixels, analytics, ads)
    if (NOISY_RESOURCE_RE.test(url)) {
      return false;
    }

    // Drop specific 403s that are not actionable
    if (event.resource?.status_code === 403 && EXPECTED_403_RE.test(url)) {
      return false;
    }
  }

  // Keep everything else
  return true;
};

/**
 * Only collect RUM from our domains and the hosts used for local/preview work.
 */
export function isAllowedRumHost(hostname?: string): boolean {
  const host = (hostname ?? getBrowserHostname())
    .toLowerCase()
    .replace(/\.$/, '');

  return (
    host === 'leesa.com' ||
    host.endsWith('.leesa.com') ||
    host === 'leesa-store-app.vercel.app' ||
    host.endsWith('-3zdev.vercel.app') ||
    host === 'localhost' ||
    host.endsWith('.localhost') ||
    host === '127.0.0.1' ||
    host === '0.0.0.0' ||
    host === '::1' ||
    host === '[::1]'
  );
}

function getBrowserHostname(): string {
  return typeof window === 'undefined' ? '' : window.location.hostname;
}

/**
 * Error messages that are generic / transient / not actionable.
 */
const NOISY_ERROR_PATTERNS: readonly RegExp[] = [
  // This emits when ad blockers block Elevar's GTM config module.
  /^Elevar GTM Error\. Could be blocked by user\./
];

// Combined pattern for noisy errors that are dropped unconditionally
const NOISY_ERROR_RE = compilePatterns(NOISY_ERROR_PATTERNS);

/**
 * Generic network/fetch error messages go here.
 * These occur when the browser couldn't complete the request. Happens on
 * connection drops, airplane mode, tab backgrounding, navigation away mid-fetch,
 * etc. Note that vercel monkey patches the fetch API so on datadog, our own
 * chunks appear in the stack trace, but the actual error originates from
 * unhandled fetches from third party scripts (GTM, etc). So, when adding new
 * patterns, use these patterns alongside other conditionals to check deeper
 * into the stack trace to identify real issues vs noise and don't drop actual
 * errors from our own code.
 */
const FETCH_ERROR_PATTERNS: readonly RegExp[] = [
  // Chromium outputs "Failed to fetch" for network errors.
  /^Failed to fetch$/

  // TODO Chrome is just the most common case. We can add more browser
  // network/fetch error patterns here after more analysis.
];

// Combined pattern for generic network/fetch errors
const FETCH_ERROR_RE = compilePatterns(FETCH_ERROR_PATTERNS);

// Stack frames that indicate the error came from GTM
const GTM_STACK_RE = /googletagmanager\.com\/gtm\.js/;

/**
 * Resource URLs we don't need error/resource events for: Place marketing pixels,
 * analytics beacons, and ad calls here. These add resource counts without
 * telling us anything about *our* app's performance. Don't add resources that
 * have actionable errors or do more than just fire pixels.
 */
const NOISY_RESOURCE_PATTERNS: readonly RegExp[] = [
  // Bugsnag JS loaded by a third-party.
  // Official CDN: d2wy8f7a9ursnm.cloudfront.net
  /cloudfront\.net.*bugsnag/
];

const NOISY_RESOURCE_RE = compilePatterns(NOISY_RESOURCE_PATTERNS);

/**
 * URLs where a 403 is expected / not actionable.
 */
const EXPECTED_403_PATTERNS: readonly RegExp[] = [
  // Bot-detection layer rejects automated cart mutations with 403.
  // These are intentional blocks, not bugs.
  /\/api\/shopify\/(addToCart|updateCart|removeFromCart|createCart)/,

  // Email subscription endpoint also bot-blocked with 403.
  /\/api\/emailSubscription/,

  // Google reCAPTCHA script can 403 on crawlers. Not actionable from our side.
  /google\.com\/recaptcha/
];

const EXPECTED_403_RE = compilePatterns(EXPECTED_403_PATTERNS);

/**
 * Combine a list of RegExp literals into one case-insensitive regex.
 * Note the combined regex is always case-insensitive.
 */
function compilePatterns(patterns: readonly RegExp[]): RegExp {
  return new RegExp(patterns.map((r) => r.source).join('|'), 'i');
}
